import React, { useState, useEffect } from 'react';
import { Mic, Edit3, ChevronRight, Bot, MessageSquare, Zap } from 'lucide-react';

const UserGuide = () => {
  const [activeSection, setActiveSection] = useState('jobotics-agent');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeSection]);

  const sections = [
    {
      id: 'jobotics-agent',
      title: 'Jobotics Agent',
      icon: Bot,
      description: 'Ask questions, attach files, get reports and answers',
    },
    {
      id: 'contract-redline',
      title: 'Contract Redline',
      icon: Edit3,
      description: 'Upload a contract and get an edited copy back',
      badge: 'NEW',
    },
    {
      id: 'voice-agent',
      title: 'PM Voice Agent',
      icon: Mic,
      description: 'Talk instead of typing — hear answers out loud',
    },
    {
      id: 'chat-history',
      title: 'Chat History',
      icon: MessageSquare,
      description: 'Find past chats and voice sessions in one place',
    },
  ];

  const getContent = () => {
    switch (activeSection) {
      case 'jobotics-agent':
        return (
          <div className="space-y-5">
            <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
              <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <Bot className="h-6 w-6 text-indigo-600" />
                Jobotics Agent
              </h3>
              <p className="text-gray-700 text-sm">
                Think of this as your project assistant. You can ask it to look things up on the web, help with labor-rate references (MCAA / NECA), attach spreadsheets or PDFs, and get back files like Excel, PDFs, slides, or draft emails. Industry and economic context is included when it helps answer your question.
              </p>

              <div>
                <h4 className="font-semibold text-gray-900 text-sm mb-2">Getting started</h4>
                <ol className="text-sm text-gray-700 space-y-2 list-decimal list-inside">
                  <li>
                    Click <strong>Jobotics Agent</strong> on the left. Sign in if you want your conversation saved—then you can find it later under <strong>Chat History</strong>.
                  </li>
                  <li>
                    Type your question (or paste lines you want help with) and press send. If you only need quick answers without attaching files, turn on <strong>Quick</strong> first—details below.
                  </li>
                  <li>
                    The reply appears bit by bit while it&apos;s working. When it&apos;s finished, tap any <strong>download</strong> or <strong>view</strong> links to open files it created. You can ask follow-ups in the same chat or start fresh when you want a clean slate.
                  </li>
                </ol>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h4 className="font-semibold text-amber-900 mb-2 text-sm flex items-center gap-2">
                  <Zap className="h-4 w-4" />
                  Quick mode (simple questions)
                </h4>
                <ul className="text-sm text-gray-800 space-y-1.5 list-disc list-inside">
                  <li>
                    Best when you don&apos;t need to upload documents—just labor lookups and web search. File upload is turned off to keep things fast.
                  </li>
                  <li>You&apos;ll see progress messages while it searches the web, then the full answer fills in.</li>
                </ul>
              </div>

              <div className="border-l-4 border-indigo-500 pl-4">
                <h4 className="font-semibold text-gray-800 mb-2 text-sm">Full mode (documents &amp; bigger tasks)</h4>
                <p className="text-sm text-gray-700">
                  Leave <strong>Quick</strong> off when you want to attach files or run a fuller workflow. You may see a short plan to approve first. Add your files, send the message, then use the links in the reply to open what it produced. Ask again if you want something changed.
                </p>
              </div>
            </div>
          </div>
        );

      case 'chat-history':
        return (
          <div className="space-y-5">
            <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
              <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <MessageSquare className="h-6 w-6 text-indigo-600" />
                Chat History
              </h3>
              <p className="text-gray-700 text-sm">
                This is your library of saved conversations—Jobotics chats, voice sessions, and (when available) contract review threads. (The small <strong>History</strong> button inside Jobotics only lists Jobotics chats.)
              </p>

              <div>
                <h4 className="font-semibold text-gray-900 text-sm mb-2">Finding and opening a chat</h4>
                <ol className="text-sm text-gray-700 space-y-2 list-decimal list-inside">
                  <li>
                    Click <strong>Chat History</strong> on the left. Your past sessions appear in the list on the left; click one to read it on the right.
                  </li>
                  <li>
                    Scroll to read the whole conversation. For long answers, use <strong>Copy full reply</strong> so you can paste it into email or a document.
                  </li>
                  <li>
                    To remove something from your list, use the trash icon on that row—it deletes only your copy of that chat (or voice session, or saved contract review).
                  </li>
                </ol>
              </div>
            </div>
          </div>
        );

      case 'contract-redline':
        return (
          <div className="space-y-5">
            <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
              <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <Edit3 className="h-6 w-6 text-blue-600" />
                Contract Redline
              </h3>
              <p className="text-gray-700 text-sm">
                Upload your agreement as a <strong>PDF</strong> or <strong>Word</strong> document. The tool highlights risks and important clauses and gives you an edited (&quot;redlined&quot;) version you can download and share. While you&apos;re signed in, each completed analysis is logged so you can reopen it from sidebar <strong>Chat History</strong>. Clear, text-based files work best; very large attachments may take longer.
              </p>

              <div>
                <h4 className="font-semibold text-gray-900 text-sm mb-2">What to do</h4>
                <ol className="text-sm text-gray-700 space-y-2 list-decimal list-inside">
                  <li>
                    <strong>Upload</strong> your file and wait until you see that it went through successfully.
                  </li>
                  <li>
                    Choose <strong>Analyze &amp; Redline</strong> and stay on the page until it finishes—usually you don&apos;t need to refresh unless something looks stuck.
                  </li>
                  <li>
                    Read the summary and notes on screen, then <strong>Download</strong> the redlined file and save it wherever you keep project documents.
                  </li>
                </ol>
              </div>
            </div>
          </div>
        );

      case 'voice-agent':
        return (
          <div className="space-y-5">
            <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
              <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <Mic className="h-6 w-6 text-blue-600" />
                PM Voice Agent
              </h3>
              <p className="text-gray-700 text-sm">
                Ask questions by speaking—useful on the jobsite or when your hands are full. You&apos;ll hear answers read aloud and may see matching details on screen. <strong>In this demo, that information is synthetic</strong>—sample project-style data for illustration, not your company&apos;s live systems or real jobs.
              </p>

              <div>
                <h4 className="font-semibold text-gray-900 text-sm mb-2">Before you begin</h4>
                <ol className="text-sm text-gray-700 space-y-2 list-decimal list-inside">
                  <li>
                    Open <strong>PM Voice Agent</strong> from the menu on a phone or computer that has a microphone.
                  </li>
                  <li>
                    When your browser asks to use the microphone, choose <strong>Allow</strong>—otherwise voice won&apos;t work.
                  </li>
                  <li>
                    Sign in with your usual account so past sessions can show up under <strong>Chat History</strong>.
                  </li>
                </ol>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 text-sm mb-2">Having a conversation</h4>
                <ol className="text-sm text-gray-700 space-y-2 list-decimal list-inside">
                  <li>
                    Tap start talking, ask your question in plain English (mention the job name if it helps), pause for a moment, then listen to the answer—you can read along on screen when text appears.
                  </li>
                  <li>
                    Ask another question in the same session if you need more. When you&apos;re done or walking away from a noisy area, stop talking so it isn&apos;t picking up background sound.
                  </li>
                </ol>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="text-center py-8">
            <p className="text-gray-500 text-sm">Pick a topic from the list on the left to get started.</p>
          </div>
        );
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex flex-col lg:flex-row gap-8">
        <div className="lg:w-80 flex-shrink-0">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-1">Guide</h3>
            <p className="text-xs text-gray-500 mb-4">Simple tips for each area</p>
            <nav className="space-y-1">
              {sections.map((section) => {
                const Icon = section.icon;
                return (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full text-left flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-colors ${
                      activeSection === section.id
                        ? 'bg-blue-100 text-blue-800 border-l-4 border-blue-600'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Icon
                      className={`h-5 w-5 mr-3 ${
                        activeSection === section.id ? 'text-blue-600' : 'text-gray-400'
                      }`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium flex items-center gap-2">
                        {section.title}
                        {section.badge && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-orange-500 text-white rounded-full">
                            {section.badge}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">{section.description}</div>
                    </div>
                    <ChevronRight
                      className={`h-4 w-4 shrink-0 ${
                        activeSection === section.id ? 'text-blue-600' : 'text-gray-400'
                      }`}
                    />
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        <div className="flex-1">
          <div className="bg-white rounded-lg border border-gray-200 min-h-96">
            <div className="p-6">{getContent()}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserGuide;
